"use client";

import { Icon } from "@focal/ui-kit";
import React, { type FC } from "react";

const SHIMMER_BG =
  "linear-gradient(90deg, #404040 0%, #404040 35%, #f5f5f5 50%, #404040 65%, #404040 100%)";

/**
 * Progress copy that advances over the wait, so a long turn doesn't read as
 * frozen. Names no subject or internal source.
 */
const STREAMING_STEPS: readonly { after: number; text: string }[] = [
  { after: 0, text: "Working on it..." },
  { after: 6000, text: "Gathering the details..." },
  { after: 15000, text: "Still pulling things together..." },
  { after: 26000, text: "Almost there..." },
];

const useAdvancingStreamingLabel = (): string => {
  const [elapsed, setElapsed] = React.useState(0);
  React.useEffect(() => {
    const startedAt = Date.now();
    const id = setInterval(() => setElapsed(Date.now() - startedAt), 1000);
    return () => clearInterval(id);
  }, []);
  let text = STREAMING_STEPS[0].text;
  for (const step of STREAMING_STEPS) {
    if (elapsed >= step.after) text = step.text;
  }
  return text;
};

interface StreamingHeaderProps {
  /**
   * What the run is actually doing, when the run can say. The deliverable chat
   * gets this off its `tool`/`status` frames; the conversation chat has no such
   * signal and leaves it out, falling back to the advancing copy above.
   */
  label?: string | null;
  "data-cy"?: string;
}

/**
 * The shimmering line at the top of a bubble that is still filling.
 *
 * It lives inside the bubble rather than beside it so a turn that says
 * something before it finishes grows downward from the status line instead of
 * replacing it.
 */
export const StreamingHeader: FC<StreamingHeaderProps> = ({
  label,
  "data-cy": dataCy,
}) => {
  const advancingLabel = useAdvancingStreamingLabel();
  return (
    <div
      className="inline-flex min-h-5 items-center gap-2"
      data-cy={dataCy}
      role="status"
      aria-live="polite"
    >
      <Icon name="AiIcon" className="size-5 shrink-0 text-blue-700" />
      <span
        className="text-sm animate-[shimmer-text_1.2s_ease-in-out_infinite] bg-[length:200%_100%]"
        style={{
          backgroundImage: SHIMMER_BG,
          WebkitBackgroundClip: "text",
          WebkitTextFillColor: "transparent",
          backgroundClip: "text",
        }}
      >
        {label ?? advancingLabel}
      </span>
    </div>
  );
};
